import { PropCategory } from "building-types/props/propCategoryTypes";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { type SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { AsyncLazy, Lazy } from "common/data/lazy";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { resolveAnnotationParameterContext } from "doc/plans/annotationParameterContext";
import {
  makeSheetGeometryFromElevationViewAnnotations,
  makeSheetGeometryFromLevelPlanAnnotations,
} from "doc/plans/planAnnotations";
import { makeSheetGeometryFromSectionViewAnnotations } from "doc/plans/planAnnotations";
import type {
  PlanGeometryGenerator,
  PlanGenerationContext,
  ElevationGenerationContext,
  SectionGenerationContext,
  SheetGeometryGenerationContext,
  LazyIntermediateData,
  SlowPlanGeometryGeneratorWithLabel,
} from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder, supplyGeometryListTagIfMissing } from "doc/plans/planGeometrySource";
import { makeSheetGeometryForCalloutsFromSectionViews } from "doc/plans/planSectionViewCallouts";
import {
  makeSheetGeometryFromSiteShapeContours,
  makeSheetGeometryFromSiteShapeDriveways,
  makeSheetGeometryFromSiteShapeElevationTags,
  makeSheetGeometryFromSiteShapeRoads,
} from "lib/house/site/planSite";
import { elevationAutoAnnotationFastGeometryGenerator } from "lib/house/svg/elevationAnnotationSVG";
import {
  makeSheetGeometryFromCaseworkNotesAndTags,
  makeSheetGeometryFromCasework,
  makeSheetGeometryFromCaseworkSolidDashed,
  makeSheetGeometryFromCaseworkPoche,
} from "./entities/planCasework";
import { makeSheetGeometryFromCeilings } from "./entities/planCeilings";
import { makeSheetGeometryFromGeneratedColumns } from "./entities/planColumns";
import {
  makeSheetGeometryForDoors,
  makeSheetGeometryForDoorsOperationLinework,
  makeSheetGeometryForDoorTrim,
  makeSheetGeometryFromDoorTagLabels,
} from "./entities/planDoors";
import {
  makeSheetGeometryFromElectricalControllers,
  makeSheetGeometryFromElectricalLinks,
  makeSheetGeometryFromElectricalLoadLabels,
  makeSheetGeometryFromElectricalLoads,
} from "./entities/planElectrical";
import {
  makeSheetGeometryFromFloorFraming,
  makeSheetGeometryFromFloorFramingLabels,
} from "./entities/planFloorFraming";
import { makeSheetGeometryLevelHeightDifferenceLines } from "./entities/planFloorHeightDifferences";
import {
  makeSheetGeometryForFloorFill,
  makeSheetGeometryFromConditionedFloorAreas,
  makeFloorMaterialGeometries,
  makeSheetGeometryForDecks,
  makeSheetGeometryFromUnconditionedFloorAreas,
} from "./entities/planFloors";
import { makeSheetGeometryFromFloorSubFlooring } from "./entities/planFloorSubFlooring";
import { makeSheetGeometryFromFloorSystemTags } from "./entities/planFloorSystemTags";
import {
  makeSheetGeometryFromFootingEdges,
  makeSheetGeometryFromCurbEdges,
  makeSheetGeometryFromStemWallUnionedEdges,
  makeSheetGeometryFromSlabLabels,
  makeSheetGeometryFromPatioSlabs,
  makeSheetGeometryFromStemWallCut,
  makeSheetGeometryFromStemWallGaps,
  makeSheetGeometryFromStemWallAbove,
  makeSheetGeometryFromSlabEdgesAbove,
  makeSheetGeometryFromSlabEdgesBelow,
  makeSheetGeometryForFoundationOutline,
  makeSheetGeometryForUtilityFoundationLinework,
  makeSheetGeometryForFoundationConditionTags,
  makeSheetGeometryFromPiers,
  makeSheetGeometryFromBrickLedgeProjected,
  makeSheetGeometryFromBrickLedgeCut,
} from "./entities/planFoundations";
import { makeSheetGeometryFromHeaderFramingTagLabels } from "./entities/planHeaderFramingTags";
import {
  makeSheetGeometryFloorPlateExteriorPerimeters,
  makeSheetGeometryWallExteriorPerimeters,
} from "./entities/planPerimeters";
import {
  makeSheetGeometryForPorchBeams,
  makeSheetGeometryForPorchColumns,
  makeSheetGeometryForPorchFloors,
} from "./entities/planPorches";
import {
  makeSheetGeometryFromPropFillForAllLevels,
  makeSheetGeometryFromPropLineworkForAllLevels,
  makeSheetGeometryFromPropTagsForAllLevels,
} from "./entities/planProps";
import { makeSheetGeometryFromRailings, makeSheetGeometryFromRailingsStairsOnly } from "./entities/planRailings";
import {
  makeSheetGeometryFromDormerFraming,
  makeSheetGeometryFromRidgeVentAnnos,
  makeSheetGeometryFromRoofOverhangDimensions,
  makeSheetGeometryFromRoofPerims,
  makeSheetGeometryFromRoofPerimsWithLevelPerims,
  makeSheetGeometryFromRoofRidgeVents,
  makeSheetGeometryFromRoofSlopeAnnos,
  makeSheetGeometryFromSkylightAnnos,
} from "./entities/planRoofs";
import {
  makeSheetGeometryForChaseRooms,
  makeSheetGeometryFromRoomLabels,
  makeSheetGeometryFromSectionRoomLabels,
} from "./entities/planRooms";
import { makeSheetGeometryFromStairCrossSections, makeSheetGeometryFromStairs } from "./entities/planStairs";
import {
  makeSheetGeometryFromWainscotingProjected,
  makeSheetGeometryFromWainscotingCut,
} from "./entities/planWainscoting";
import {
  makeSheetGeometryFromWallSubstrate,
  makeSheetGeometryForWallPlaneMasses,
  makeSheetGeometryForWallExteriorFinish,
  makeSheetGeometryForWallExteriorFinishProjected,
  makeSheetGeometryForWallBoundaries,
} from "./entities/planWalls";
import {
  makeSheetGeometryForWindows,
  makeSheetGeometryForWindowCutTrim,
  makeSheetGeometryFromWindowTagLabels,
  makeSheetGeometryForWindowProjectedTrim,
} from "./entities/planWindows";
import { makeSheetGeometryFromExteriorDimensionsByLevel } from "./exteriorDimensionsSheetGeometry";
import {
  makePlanGeometryFromUtilityDimensions,
  makeSheetGeometryFromFoundationDimensionsByLevel,
} from "./foundationDimensionsSheetGeometry";
import { makePlanGeometryFromFoundationPropLinework } from "./foundationPropLinework";
import { elevationAutoAnnotationSlowGeometryGenerator } from "./initElevationContextIntermediateData";
import { makeSheetGeometryFromInteriorDimensionsByLevel } from "./interiorDimensionsSheetGeometry";
import { makeSheetGeometryFromSectionGradeLine } from "./sectionGradeLine";

function addAnnotationsWithPrecision(ctx: PlanGenerationContext, includeAllLevels: boolean) {
  const builder = new PlanGeometryBuilder();
  const { dimensionPrecision, planRotationRads, annotationParameterContext } = ctx;
  const result = makeSheetGeometryFromLevelPlanAnnotations({
    building: ctx.building,
    levelId: ctx.levelId,
    includeAllLevels,
    annotationOptions: { dimensionPrecision, includeAllLevels, planRotationRads },
    globalParameterValues: ctx.globalParameterValues,
    annotationParameterContext,
  });
  builder.addAllFrom(result);
  return builder;
}

type ManualAnnotationIntermediateData = Required<Pick<LazyIntermediateData, "manualAnnotations">>;
type ElevationAsyncAutoAnnotationIntermediateData = Required<
  Pick<LazyIntermediateData, "elevationAsyncAutoAnnotations">
>;
type ElevationAutoAnnotationIntermediateData = Required<Pick<LazyIntermediateData, "elevationAutoAnnotations">>;
type ElevationAnnotationIntermediateData = Required<
  Pick<LazyIntermediateData, "elevationAsyncAutoAnnotations" | "elevationAutoAnnotations" | "manualAnnotations">
>;

type PlanContextIntermediateData = Required<LazyIntermediateData>;

export function initPlanContextIntermediateData(context: PlanGenerationContext): PlanContextIntermediateData {
  const annotationParameterContext =
    context.annotationParameterContext ??
    resolveAnnotationParameterContext(context.building, context.globalParameterValues ?? {});
  const contextWithAnnotationParameters: PlanGenerationContext = {
    ...context,
    annotationParameterContext,
  };

  return {
    foundationLinework: new AsyncLazy(() =>
      makePlanGeometryFromFoundationPropLinework(contextWithAnnotationParameters),
    ),
    exteriorDimensions: new Lazy(() => makeSheetGeometryFromExteriorDimensionsByLevel(contextWithAnnotationParameters)),
    interiorDimensions: new Lazy(() => makeSheetGeometryFromInteriorDimensionsByLevel(contextWithAnnotationParameters)),
    foundationDimensions: new Lazy(() =>
      makeSheetGeometryFromFoundationDimensionsByLevel(contextWithAnnotationParameters),
    ),
    utilityDimensions: new AsyncLazy(() => makePlanGeometryFromUtilityDimensions(contextWithAnnotationParameters)),
    manualAnnotations: new Lazy(() => addAnnotationsWithPrecision(contextWithAnnotationParameters, false)),
    manualAnnotationsAllLevels: new Lazy(() => addAnnotationsWithPrecision(contextWithAnnotationParameters, true)),
    railings: new Lazy(() => makeSheetGeometryFromRailings(contextWithAnnotationParameters)),
    railingsStairOnly: new Lazy(() => makeSheetGeometryFromRailingsStairsOnly(contextWithAnnotationParameters)),
    piers: new Lazy(() => makeSheetGeometryFromPiers(contextWithAnnotationParameters)),
    roofOverhangDimensions: new Lazy(() =>
      makeSheetGeometryFromRoofOverhangDimensions(contextWithAnnotationParameters),
    ),
    elevationAsyncAutoAnnotations: new AsyncLazy(async () => {
      return { taggedGeometries: {} };
    }),
    elevationAutoAnnotations: new AsyncLazy(async () => {
      return { taggedGeometries: {} };
    }),
    stairs: new Lazy(() => makeSheetGeometryFromStairs(contextWithAnnotationParameters)), // inline styles
  };
}

const ManualAnnotationIntermediateDataFieldMap: SheetGeometryCategoryTable<keyof ManualAnnotationIntermediateData> = {
  ANNOT_LINEWORK: "manualAnnotations",
  ANNOT_DETAIL: "manualAnnotations",
  ANNOT_NOTE: "manualAnnotations",

  ANNOT_DIM_GROUP: "manualAnnotations",
  ANNOT_DIM_LABEL: "manualAnnotations",
  ANNOT_DIM_LINE: "manualAnnotations",
  ANNOT_DIM_LINEWORK: "manualAnnotations",
  ANNOT_DIM_TICK: "manualAnnotations",
  ANNOT_DIM_WITNESS: "manualAnnotations",
};

const ElevationAutoAnnotationIntermediateDataFieldMap: SheetGeometryCategoryTable<
  keyof ElevationAutoAnnotationIntermediateData
> = {
  TOP_OF_PLATE_MARKERS: "elevationAutoAnnotations",
  TOP_OF_PLATE_DIMENSIONS: "elevationAutoAnnotations",
  DORMER_WINDOW_VERTICAL_DIMENSIONS: "elevationAutoAnnotations",
  DORMER_WINDOW_TAGS: "elevationAutoAnnotations",
  ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS: "elevationAutoAnnotations",
  ROOF_GABLE_WINDOW_TAGS: "elevationAutoAnnotations",
  ROOF_DIM_ANNOTATION: "elevationAutoAnnotations",
  ROOF_SLOPE_SYMBOL: "elevationAutoAnnotations",
  WINDOW_HEADER_ANNOTATION: "elevationAutoAnnotations",
  ELEVATION_MARKERS: "elevationAutoAnnotations",
  GRADE_LINE: "elevationAutoAnnotations",
};

const ElevationAnnotationIntermediateDataFieldMap: SheetGeometryCategoryTable<
  keyof ElevationAnnotationIntermediateData
> = {
  ...ManualAnnotationIntermediateDataFieldMap,
  ...ElevationAutoAnnotationIntermediateDataFieldMap,
};

const ElevationAsyncAutoAnnotationIntermediateDataFieldMap: SheetGeometryCategoryTable<
  keyof ElevationAsyncAutoAnnotationIntermediateData
> = {
  MATERIAL_TAGS: "elevationAsyncAutoAnnotations",
};

const PlanGenerationContextIntermediateDataFieldMap: SheetGeometryCategoryTable<keyof PlanContextIntermediateData> = {
  FOUNDATION_SOLID_LINEWORK: "foundationLinework",
  FOUNDATION_DASHED_LINEWORK: "foundationLinework",

  WALL_EXTERIOR_DIM_GROUP: "exteriorDimensions",
  WALL_EXTERIOR_DIM_LABEL: "exteriorDimensions",
  WALL_EXTERIOR_DIM_LINEWORK: "exteriorDimensions",
  WALL_EXTERIOR_DIM_TICK: "exteriorDimensions",
  WALL_EXTERIOR_DIM_WITNESS: "exteriorDimensions",
  WALL_EXTERIOR_DIM_LINE: "exteriorDimensions",

  WALL_INTERIOR_DIM_GROUP: "interiorDimensions",
  WALL_INTERIOR_DIM_LABEL: "interiorDimensions",
  WALL_INTERIOR_DIM_LINEWORK: "interiorDimensions",
  WALL_INTERIOR_DIM_TICK: "interiorDimensions",
  WALL_INTERIOR_DIM_WITNESS: "interiorDimensions",
  WALL_INTERIOR_DIM_LINE: "interiorDimensions",

  FOUNDATION_DIM_GROUP: "foundationDimensions",
  FOUNDATION_DIM_LABEL: "foundationDimensions",
  FOUNDATION_DIM_LINEWORK: "foundationDimensions",
  FOUNDATION_DIM_TICK: "foundationDimensions",
  FOUNDATION_DIM_WITNESS: "foundationDimensions",
  FOUNDATION_DIM_LINE: "foundationDimensions",

  ROOF_OVERHANG_DIM_GROUP: "roofOverhangDimensions",
  ROOF_OVERHANG_DIM_LABEL: "roofOverhangDimensions",
  ROOF_OVERHANG_DIM_LINEWORK: "roofOverhangDimensions",
  ROOF_OVERHANG_DIM_TICK: "roofOverhangDimensions",
  ROOF_OVERHANG_DIM_WITNESS: "roofOverhangDimensions",
  ROOF_OVERHANG_DIM_LINE: "roofOverhangDimensions",

  UTILITY_DIM_LABEL: "utilityDimensions",
  UTILITY_DIM_LINEWORK: "utilityDimensions",
  UTILITY_DIM_TICK: "utilityDimensions",
  UTILITY_DIM_WITNESS: "utilityDimensions",

  ...ManualAnnotationIntermediateDataFieldMap,

  ALL_LVL_ANNOT_LINEWORK: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DETAIL: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_NOTE: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_GROUP: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_LABEL: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_LINE: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_LINEWORK: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_TICK: "manualAnnotationsAllLevels",
  ALL_LVL_ANNOT_DIM_WITNESS: "manualAnnotationsAllLevels",

  RAILING_PROJECTED: "railings",
  RAILING_ABOVE: "railings",
  RAILING_PROJECTED_STAIR_ONLY: "railingsStairOnly",
  RAILING_ABOVE_STAIR_ONLY: "railingsStairOnly",
  RAILING_POST: "railings",
  RAILING_POST_STAIR_ONLY: "railingsStairOnly",

  PIER: "piers",
  PIER_HIDDEN: "piers",

  STAIR_ABOVE: "stairs",
  STAIR_BREAK_LINE: "stairs",
  STAIR_PROJECTED: "stairs",
};

const PlanGenerationContextSingleResultGenerators: SheetGeometryCategoryTable<
  (ctx: PlanGenerationContext) => SheetGeometry[] | Promise<SheetGeometry[]>
> = {
  BRICKLEDGE_CUT: makeSheetGeometryFromBrickLedgeCut,
  BRICKLEDGE_PROJECTED: makeSheetGeometryFromBrickLedgeProjected,
  CASEWORK_POCHE: makeSheetGeometryFromCaseworkPoche,
  CASEWORK_SOLID_DASH: makeSheetGeometryFromCaseworkSolidDashed,
  CASEWORK_TAG: makeSheetGeometryFromCaseworkNotesAndTags,
  CASEWORK: makeSheetGeometryFromCasework,
  CEILING: makeSheetGeometryFromCeilings,
  CHASE_ROOMS: makeSheetGeometryForChaseRooms,
  COLUMN: makeSheetGeometryFromGeneratedColumns,
  CONDITION_TAG: makeSheetGeometryForFoundationConditionTags,
  DECK: makeSheetGeometryForDecks,
  FLOOR_FILL: makeSheetGeometryForFloorFill,
  DOOR_CASING_CUT: makeSheetGeometryForDoorTrim,
  DOOR_OPERATION_LINEWORK: makeSheetGeometryForDoorsOperationLinework,
  DOOR_TAGS: makeSheetGeometryFromDoorTagLabels,
  DOORS: makeSheetGeometryForDoors,
  ELECTRICAL_CONTROLLERS: makeSheetGeometryFromElectricalControllers,
  ELECTRICAL_LINKS: makeSheetGeometryFromElectricalLinks,
  ELECTRICAL_LOADS: makeSheetGeometryFromElectricalLoads,
  ELECTRIC_LOAD_LABEL: makeSheetGeometryFromElectricalLoadLabels,
  FLOOR_AREA_CONDITIONED: makeSheetGeometryFromConditionedFloorAreas,
  FLOOR_AREA_UNCONDITIONED: makeSheetGeometryFromUnconditionedFloorAreas,
  FLOOR_FINISH: makeFloorMaterialGeometries,
  FLOOR_FRAMING_LABEL: makeSheetGeometryFromFloorFramingLabels,
  FLOOR_SYSTEM_TAG: makeSheetGeometryFromFloorSystemTags,
  FLOOR_FRAMING: makeSheetGeometryFromFloorFraming,
  FOOTING: makeSheetGeometryFromFootingEdges,
  FOUNDATION_CURB: makeSheetGeometryFromCurbEdges,
  FOUNDATION_OUTLINE: makeSheetGeometryForFoundationOutline,
  PERIMS_FLOORPLATE: makeSheetGeometryFloorPlateExteriorPerimeters,
  FLOOR_HEIGHT_DIFFERENCE: makeSheetGeometryLevelHeightDifferenceLines,
  PERIMS_WALL: makeSheetGeometryWallExteriorPerimeters,
  PORCH_BEAMS: makeSheetGeometryForPorchBeams,
  PORCH_COLUMNS: makeSheetGeometryForPorchColumns,
  PORCH: makeSheetGeometryForPorchFloors,
  PROP_FILL: (ctx) => makeSheetGeometryFromPropFillForAllLevels(ctx),
  PROP_TAG: makeSheetGeometryFromPropTagsForAllLevels,
  PROP_APPLIANCES: (ctx) => makeSheetGeometryFromPropLineworkForAllLevels({ ...ctx, type: PropCategory.APPLIANCES }),
  PROP_FURNITURE: (ctx) => makeSheetGeometryFromPropLineworkForAllLevels({ ...ctx, type: PropCategory.FURNITURE }),
  PROP_FIXTURES: (ctx) => makeSheetGeometryFromPropLineworkForAllLevels({ ...ctx, type: PropCategory.FIXTURES }),
  PROP_OTHER: (ctx) => makeSheetGeometryFromPropLineworkForAllLevels({ ...ctx, type: PropCategory.OTHER }),
  PROP_FOLIAGE: async (_ctx) => [],
  ROOF_DORMER_FRAMING: makeSheetGeometryFromDormerFraming,
  ROOF_PERIMS_WITH_LEVEL_PERIMS: makeSheetGeometryFromRoofPerimsWithLevelPerims,
  ROOF_PERIMS: makeSheetGeometryFromRoofPerims,
  ROOF_RIDGE_VENT: makeSheetGeometryFromRoofRidgeVents,
  ROOF_RIDGE_VENT_ANNOS: makeSheetGeometryFromRidgeVentAnnos,
  ROOF_SKYLIGHT_ANNOS: makeSheetGeometryFromSkylightAnnos,
  ROOF_SLOPE_ANNOS: makeSheetGeometryFromRoofSlopeAnnos,
  ROOM_LABELS: makeSheetGeometryFromRoomLabels,
  SECTION_VIEW_CALLOUT: makeSheetGeometryForCalloutsFromSectionViews,
  SLAB_ABOVE: makeSheetGeometryFromSlabEdgesAbove,
  SLAB_BELOW: makeSheetGeometryFromSlabEdgesBelow,
  SLAB_PATIO: makeSheetGeometryFromPatioSlabs,
  SLAB_TAG: makeSheetGeometryFromSlabLabels,
  STAIR_WALLS: makeSheetGeometryFromStairCrossSections,
  STEM_WALL_ABOVE_GAPS: makeSheetGeometryFromStemWallGaps,
  STEM_WALL_ABOVE: makeSheetGeometryFromStemWallAbove,
  STEM_WALL_BELOW: makeSheetGeometryFromStemWallCut,
  STEM_WALL_UNIONED_EDGES: makeSheetGeometryFromStemWallUnionedEdges,
  SUB_FLOORING: makeSheetGeometryFromFloorSubFlooring,
  SITE_SHAPE_ROADS: makeSheetGeometryFromSiteShapeRoads,
  SITE_SHAPE_CONTOURS: makeSheetGeometryFromSiteShapeContours,
  SITE_SHAPE_ELEVATION_TAGS: makeSheetGeometryFromSiteShapeElevationTags,
  SITE_SHAPE_DRIVEWAYS: makeSheetGeometryFromSiteShapeDriveways,
  UTILITY_FOUNDATION_LINEWORK: makeSheetGeometryForUtilityFoundationLinework,
  WAINSCOTING_CUT: makeSheetGeometryFromWainscotingCut,
  WAINSCOTING_PROJECTED: makeSheetGeometryFromWainscotingProjected,
  WALL_BOUNDARIES: makeSheetGeometryForWallBoundaries,
  WALL_CORE: makeSheetGeometryForWallPlaneMasses,
  WALL_EXTERIOR_FINISH: makeSheetGeometryForWallExteriorFinish,
  WALL_EXTERIOR_FINISH_PROJECTED: makeSheetGeometryForWallExteriorFinishProjected,
  WALL_SUBSTRATE: makeSheetGeometryFromWallSubstrate,
  WINDOW_CASING_CUT: makeSheetGeometryForWindowCutTrim,
  WINDOW_CASING_PROJECTED: makeSheetGeometryForWindowProjectedTrim,
  WINDOW_TAGS: makeSheetGeometryFromWindowTagLabels,
  HEADER_FRAMING_TAGS: makeSheetGeometryFromHeaderFramingTagLabels,
  WINDOWS: makeSheetGeometryForWindows,
};

function makeSingleResultGenerators<C extends SheetGeometryGenerationContext>(
  generators: SheetGeometryCategoryTable<(ctx: C) => SheetGeometry[] | Promise<SheetGeometry[]>>,
): SheetGeometryCategoryTable<PlanGeometryGenerator<C>> {
  const result = {};

  for (const category in generators) {
    const generator = generators[category];

    result[category] = async (ctx: C) => ({
      taggedGeometries: {
        [category]: supplyGeometryListTagIfMissing(await generator(ctx), category as SheetGeometryCategory),
      },
    });
  }

  return result;
}

/**
 * Constructs a mapping of geometry categories to intermediate data fields.
 */
function makeMultiResultGenerators<C extends SheetGeometryGenerationContext>(
  intermediateDataFieldMap: SheetGeometryCategoryTable<keyof LazyIntermediateData>,
): SheetGeometryCategoryTable<PlanGeometryGenerator<C>> {
  const generators = {};

  for (const category in intermediateDataFieldMap) {
    const fieldName = intermediateDataFieldMap[category] as keyof LazyIntermediateData;

    assertIsNotUndefined(fieldName, `No intermediate data field found for category ${category}`);

    generators[category] = async (_ctx: C, intermediateData: LazyIntermediateData) =>
      (await intermediateData[fieldName]?.value) ?? { taggedGeometries: {} };
  }

  return generators;
}

/**
 * Constructs a mapping of geometry categories to intermediate data fields.
 */
function makeGeneratorsWithLabelsUsingIntermediateData<
  C extends SheetGeometryGenerationContext,
>(intermediateDataFieldMap: {
  [key in SheetGeometryCategory]?: string;
}): SheetGeometryCategoryTable<SlowPlanGeometryGeneratorWithLabel<C>> {
  const generators: SheetGeometryCategoryTable<SlowPlanGeometryGeneratorWithLabel<C>> = {};

  for (const category in intermediateDataFieldMap) {
    const fieldName = intermediateDataFieldMap[category] as keyof LazyIntermediateData;

    assertIsNotUndefined(fieldName, `No intermediate data field found for category ${category}`);

    generators[category] = {
      slowGenerator: async (_ctx: C, intermediateData: LazyIntermediateData) =>
        (await intermediateData[fieldName])?.value ?? { taggedGeometries: {} },
      sharedGeneratorLabel: fieldName,
    };
  }

  return generators;
}

export const planGeometryGenerators: { [key in SheetGeometryCategory]?: PlanGeometryGenerator<PlanGenerationContext> } =
  {
    ...makeMultiResultGenerators(PlanGenerationContextIntermediateDataFieldMap),
    ...makeSingleResultGenerators(PlanGenerationContextSingleResultGenerators),
  };

export function initElevationContextIntermediateData(
  context: ElevationGenerationContext,
): ElevationAnnotationIntermediateData {
  return {
    manualAnnotations: new Lazy(() => makeSheetGeometryFromElevationViewAnnotations(context)),
    elevationAsyncAutoAnnotations: new AsyncLazy(async () => elevationAutoAnnotationSlowGeometryGenerator(context)),
    elevationAutoAnnotations: new AsyncLazy(async () => await elevationAutoAnnotationFastGeometryGenerator(context)),
  };
}

// Note that this includes only manual elevation annotations, not the automated ones.
export const elevationGeometryGenerators: {
  [key in SheetGeometryCategory]?: PlanGeometryGenerator<ElevationGenerationContext>;
} = makeMultiResultGenerators(ElevationAnnotationIntermediateDataFieldMap);

export function initSectionContextIntermediateData(
  context: SectionGenerationContext,
): ManualAnnotationIntermediateData {
  return {
    manualAnnotations: new Lazy(() => makeSheetGeometryFromSectionViewAnnotations(context)),
  };
}

export const slowElevationGeometryGenerators = makeGeneratorsWithLabelsUsingIntermediateData(
  ElevationAsyncAutoAnnotationIntermediateDataFieldMap,
);

export const sectionGeometryGenerators: {
  [key in SheetGeometryCategory]?: PlanGeometryGenerator<SectionGenerationContext>;
} = {
  ...makeMultiResultGenerators(ManualAnnotationIntermediateDataFieldMap),
  ...makeSingleResultGenerators<SectionGenerationContext>({
    GRADE_LINE: makeSheetGeometryFromSectionGradeLine,
    ROOM_LABELS: makeSheetGeometryFromSectionRoomLabels,
  }),
};
